import React, { useContext, useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Pages.css';
import { LanguageContext } from '../App';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { FIRM_INFO } from '../constants/firmInfo';
import { markContactFlowCompleted, setPostAuthRedirect } from '../utils/guestIntake';
import { sendLeadToZapier } from '../utils/zapier';
import { translations } from '../utils/translations';

function ContactFlow() {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const contactFlowContent = t.contactFlow;
  const { user } = useAuth();
  const location = useLocation();
  const locationState = location.state || null;
  const data = locationState || {};
  const params = new URLSearchParams(location.search);
  const isGuest = params.get('guest') === 'true';
  const skipZapier = Boolean(data.skipZapier);
  const hasSubmittedData = Boolean(
    data.name || data.email || data.phone || data.message || data.language
  );
  const [intakeData, setIntakeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadIntakeData = async () => {
      let nextIntakeData = locationState ? { ...locationState } : null;

      try {
        if (user && db) {
          const [intakeDoc, userDoc] = await Promise.all([
            getDoc(doc(db, 'intake', user.uid)),
            getDoc(doc(db, 'users', user.uid)),
          ]);

          nextIntakeData = {
            ...(userDoc.exists() ? userDoc.data() : {}),
            ...(intakeDoc.exists() ? intakeDoc.data() : {}),
            ...(locationState || {}),
          };
        } else if (isGuest) {
          const stored = localStorage.getItem('guest_intake');
          const guestIntake = stored ? JSON.parse(stored) : null;
          nextIntakeData = guestIntake
            ? {
                ...guestIntake,
                ...(locationState || {}),
              }
            : locationState;
        }
      } catch (error) {
        nextIntakeData = locationState;
      } finally {
        if (isActive) {
          setIntakeData(nextIntakeData);
          setIsLoading(false);
        }
      }
    };

    loadIntakeData();

    return () => {
      isActive = false;
    };
  }, [hasSubmittedData, isGuest, locationState, user]);

  const resolvedName = data.name || intakeData?.name || user?.displayName || '';
  const resolvedEmail = data.email || intakeData?.email || user?.email || '';
  const resolvedPhone = data.phone || intakeData?.phone || '';
  const resolvedLanguage =
    data.language || intakeData?.preferredLanguage || intakeData?.language || '';
  const resolvedScenario = data.scenario || intakeData?.scenario || '';
  const resolvedMessage =
    data.message ||
    intakeData?.situationDescription ||
    intakeData?.description ||
    intakeData?.notes ||
    intakeData?.message ||
    '';
  const hasPrefillData = Boolean(
    resolvedName ||
      resolvedEmail ||
      resolvedPhone ||
      resolvedLanguage ||
      resolvedMessage
  );
  const fallbackGuest = contactFlowContent.defaults.guest;
  const fallbackNotProvided = contactFlowContent.defaults.notProvided;
  const preferredLanguageMap = {
    English: t.header.languageOptions.en,
    Spanish: t.header.languageOptions.es,
    'Haitian Creole': t.header.languageOptions.ht,
    Portuguese: t.header.languageOptions.pt,
    Chinese: t.header.languageOptions.zh,
    Other: t.intake.other,
  };
  const displayPreferredLanguage =
    preferredLanguageMap[resolvedLanguage] || resolvedLanguage || fallbackNotProvided;

  useEffect(() => {
    if (isLoading || (!hasSubmittedData && !hasPrefillData) || sent) {
      return;
    }

    const name = resolvedName?.trim() || fallbackGuest;
    const email = resolvedEmail?.trim() || '';
    const phone = resolvedPhone?.trim() || '';
    const preferredLanguage = displayPreferredLanguage?.trim() || fallbackNotProvided;
    const scenario = resolvedScenario?.trim() || '';
    const message = resolvedMessage?.trim() || '';

    const leadData = {
      name,
      email,
      phone,
      language: preferredLanguage,
      notes: message,
      message,
      scenario,
      createdAt: new Date().toISOString(),
    };
    const emailBody = [
      contactFlowContent.emailDraft.greeting,
      '',
      contactFlowContent.emailDraft.intro,
      '',
      `${contactFlowContent.labels.name}: ${name}`,
      `${contactFlowContent.labels.email}: ${email || fallbackNotProvided}`,
      `${contactFlowContent.labels.phone}: ${phone || fallbackNotProvided}`,
      `${contactFlowContent.labels.language}: ${preferredLanguage}`,
      '',
      contactFlowContent.emailDraft.detailsLabel,
      message || fallbackNotProvided,
      '',
      contactFlowContent.emailDraft.pleaseContact,
      '',
      contactFlowContent.emailDraft.thanks,
    ]
      .filter(Boolean)
      .join('\n');

    setSent(true);

    const openMailto = async () => {
      if (!skipZapier) {
        await sendLeadToZapier(leadData);
      }

      window.location.href = `mailto:${FIRM_INFO.email}?subject=${encodeURIComponent(
        contactFlowContent.emailDraft.subject
      )}&body=${encodeURIComponent(emailBody)}`;
    };

    void openMailto();

    return undefined;
  }, [
    hasSubmittedData,
    hasPrefillData,
    isLoading,
    resolvedEmail,
    resolvedLanguage,
    resolvedMessage,
    resolvedName,
    resolvedPhone,
    resolvedScenario,
    displayPreferredLanguage,
    sent,
    skipZapier,
    fallbackGuest,
    fallbackNotProvided,
    contactFlowContent,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const authUser = auth.currentUser;

      if (authUser) {
        navigate('/dashboard', { replace: true });
      } else {
        markContactFlowCompleted();
        setPostAuthRedirect('/dashboard');
        navigate('/register', { replace: true });
      }
    }, 4000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="legal-page">
          <div className="legal-card">
            <h2>{contactFlowContent.title}</h2>
            <p className="contact-flow-status">
              {isLoading
                ? contactFlowContent.statusPreparing
                : !hasPrefillData
                  ? contactFlowContent.statusMissing
                  : !user && isGuest
                    ? contactFlowContent.statusGuest
                    : contactFlowContent.statusStarted}
            </p>

            <div className="contact-flow-summary">
              <p>
                <strong>{contactFlowContent.labels.name}:</strong> {resolvedName || fallbackGuest}
              </p>
              <p>
                <strong>{contactFlowContent.labels.email}:</strong>{' '}
                {resolvedEmail || fallbackNotProvided}
              </p>
              <p>
                <strong>{contactFlowContent.labels.phone}:</strong>{' '}
                {resolvedPhone || fallbackNotProvided}
              </p>
              <p>
                <strong>{contactFlowContent.labels.language}:</strong>{' '}
                {displayPreferredLanguage}
              </p>
              <p>
                <strong>{contactFlowContent.labels.scenario}:</strong>{' '}
                {resolvedScenario || fallbackNotProvided}
              </p>
              <p>
                <strong>{contactFlowContent.labels.details}:</strong>{' '}
                {resolvedMessage || fallbackNotProvided}
              </p>
            </div>

            <div className="legal-actions">
              <Link
                to={user ? '/dashboard' : '/register'}
                className="legal-link"
                onClick={() => {
                  if (!user) {
                    setPostAuthRedirect('/dashboard');
                  }
                }}
              >
                {user
                  ? contactFlowContent.actions.returnToDashboard
                  : contactFlowContent.actions.createAccount}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactFlow;
